import React, {
	useCallback,
} from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';

import ColumnsConfigurator from '~/components/logic/columnsConfigurator/ColumnsConfigurator';
import ColumnsConfiguratorOverlayLayout from '~/components/patterns/columnsConfigurator/ColumnsConfiguratorOverlayLayout';
import MarginsList from '../../atoms/lists/MarginsList';
import PageAttributesCategoryBox from './pagesCharts/components/PageAttributesCategoryBox';
import ScreenBody from '~/components/patterns/screens/parts/body/ScreenBody';
import ScreenBodyLayout, {
	ScreenBodyLayoutPosition,
} from '~/components/patterns/screens/parts/body/ScreenBodyLayout';

import useEnabledWebsiteColumnsList from '~/hooks/useEnabledWebsiteColumnsList';
import useViewportWidth from '~/hooks/useViewportWidth';
import useWebsiteId from '~/hooks/useWebsiteId';

import {
	closeColumnsConfigurator,
} from '~/actions';

import {
	updatePagesFilter,
} from '~/actions/pages/overview';

import {
	GRAPHABLE_FIELDS,
} from '~/model/pages';

import {
	AllCategories,
	getColumnCategory,
} from '~/model/pagesColumns';


import {
	columnsConfiguratorVisibilitySelector,
	filterSelector,
} from '~/state/ui/selectors';
import PagesGraphsSidebar from './PagesGraphsSidebar';



const VISIBLE_FILTER_SIDEBAR_BREAKPOINT = 1000;



const PagesGraphs: React.FC = () => {
	const dispatch = useDispatch();
	const websiteId = useWebsiteId();

	const columns = useEnabledWebsiteColumnsList();
	const viewportWidth = useViewportWidth();

	const columnsConfiguratorVisibility = useSelector(columnsConfiguratorVisibilitySelector);
	const filter = useSelector(filterSelector);

	const handleCloseColumnsConfigurator = useCallback(
		() => {
			dispatch(closeColumnsConfigurator());
		},
		[
			dispatch,
		],
	);

	const handleFilterChange = useCallback(
		(filter) => {
			dispatch(updatePagesFilter(filter));
		},
		[
			dispatch,
		],
	);

	const visibleColumns = columns.filter((column) => !!column && GRAPHABLE_FIELDS.includes(column)).toJS();

	const content = AllCategories.map((category) => {
		const categoryColumns = visibleColumns.filter((column) => getColumnCategory(column) === category);

		if (categoryColumns.size === 0) {
			return null;
		}

		return (
			<PageAttributesCategoryBox
				category={category}
				categoryColumns={categoryColumns}
				filter={filter}
				key={category}
				websiteId={websiteId}
			/>
		);
	});

	let contentComponent = (
		<MarginsList>
			{content}
		</MarginsList>
	);

	// filtering sidebar will be only visible when we have space for it
	if (viewportWidth < VISIBLE_FILTER_SIDEBAR_BREAKPOINT) {
		contentComponent = (
			<ScreenBody>
				{contentComponent}
			</ScreenBody>
		);
	} else {
		contentComponent = (
			<ScreenBodyLayout
				sidebar={(
					<PagesGraphsSidebar
						columns={columns.toJS()}
						onFilterChange={handleFilterChange}
						websiteId={websiteId}
					/>
				)}
				sidebarPosition={ScreenBodyLayoutPosition.Right}
				sidebarWidth={330}
			>
				<ScreenBody>
					{contentComponent}
				</ScreenBody>
			</ScreenBodyLayout>
		);
	}

	return (
		<ColumnsConfiguratorOverlayLayout
			columnsConfigurator={(
				<ColumnsConfigurator />
			)}
			isOpen={columnsConfiguratorVisibility}
			onClose={handleCloseColumnsConfigurator}
		>
			{contentComponent}
		</ColumnsConfiguratorOverlayLayout>
	);
};



export default React.memo(PagesGraphs);
